// Scherm 8 — het skill-detailpaneel.
//
// Spiegel van TrendDetail, met dezelfde maten en dezelfde volgorde. Wat de
// dummy hier hardcodeerde, staat nu per skill in de data (W16 A1):
//  · de alinea "As this domain evolves..." (V1 r. 1303) was ÉÉN tekst voor élke
//    skill -> vervangen door de echte `description`
//  · het `scoreR`-blok (V1 r. 1270) was één motivering voor élke skill ->
//    vervangen door `scoreRationale`, per dimensie per skill
//  · "Available training" en "Funding" waren verzonnen -> "binnenkort" mét reden
//    (Funding sinds W5, 06-10: de open regelingen voor de sector van de skill,
//    als afgeleide suggestie — zie SkillRegelingen.tsx)
//
// OWNER-REVIEW RONDE 4 (2026-08-24), punt Skills #5 — drie wijzigingen:
//
//  1. HET BEWIJS STAAT ER NU, en het is echt. De vorige versie had geen
//     bewijsblok met als reden "er is geen skill_evidence-tabel". Die tabel is
//     er inderdaad niet, maar het bewijs wél: élke occurrence draagt een
//     letterlijk citaat plus het document waar het uit komt. Gemeten: 1.748 van
//     de 1.762 vaardigheden. De kaarten zijn doorklikbaar naar het brondetail —
//     dat was de expliciete vraag ("evidence waar je ook op kan doorklikken").
//     De twee kaarten in de dummy (V1 r. 1390) waren verzonnen tekst; deze zijn
//     na te lezen in de bron.
//
//  2. "METING" IS WEG. Dat blok toonde bron- en trendtelling plus een regel
//     over "één meetpunt", en de owner zei het niet te begrijpen. Terecht: het
//     was een kaart óver de meting in plaats van de meting zelf. De twee
//     getallen staan nu in het drieluik bovenaan (V1 r. 1319-1338), waar ze bij
//     horen, en het derde getal is het bewijs — dat je nu ook kunt openen.
//
//  3. "WAAROM DEZE VAARDIGHEID" HEET NU "VOORTGEKOMEN UIT DEZE TRENDS".
//     Dezelfde data, maar de kop zei niet wat eronder stond. En "Wat je straks
//     kunt" staat nu bovenaan bij de omschrijving, zoals in de dummy, in plaats
//     van verstopt onder de scores.
//
// Anders dan bij trends dragen skill-TEKSTEN geen {n}-markers: de detailpas
// schrijft bewust zonder bronverwijzing in de lopende tekst (W16 A1). Dat is
// iets anders dan geen bronnen hebben — vandaar dat het bewijsblok los staat.

import { type CSSProperties, useEffect, useRef, useState } from "react";

import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { BevindingenPerTrendOud } from "./TrendlaagOud";
import { BetaalmuurPil, Bronregel, heeftBewijsvelden } from "./Bewijs";
import { ComingSoon, EmptyReason } from "./Empty";
import { SkillRegelingen } from "./SkillRegelingen";
import { InfoIcon, PanelHeader, ReasonBadge, SectionLabel } from "./ui";
import type { Skill, SkillTrendRef } from "../contracts";
import { ONDERBOUWING_ONTBREEKT, TREDE_LABEL, TREDE_UITLEG } from "../lib/schermen";
// Kleuren via de chattokens: in de oude weergave dezelfde waarden, in de
// nieuwe volgen ze licht en donker (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";

/** Een cijfer met één decimaal en een komma, zoals in het Nederlands (testfeedback 03-11, rij 6). */
const komma = (n: number) => n.toFixed(1).replace(".", ",");

type ScoreKey = "impact" | "effort" | "trending" | "urgency";
type Evidence = NonNullable<Skill["evidence"]>[number];

// Kleuren letterlijk uit de dummy; effort is het enige cijfer waar LAAG goed is,
// dus die krijgt geen "hoger is beter"-kleur maar de neutrale grijs.
const SCORE_CARDS: { key: ScoreKey; label: string; color: string; hint: string }[] = [
  {
    key: "impact",
    label: "Impact",
    color: color.accentGreen,
    hint: "Hoe breed deze vaardigheid het werk raakt.",
  },
  {
    key: "effort",
    label: "Effort",
    color: color.soft,
    hint: "Hoeveel investering het kost om hem te beheersen — hier is laag juist goed.",
  },
  {
    key: "trending",
    label: "Trending",
    color: color.accentBlue,
    hint: "Hoe hard het signaal onder deze vaardigheid groeit.",
  },
  {
    key: "urgency",
    label: "Urgentie",
    color: color.accentAmber,
    hint: "Hoe snel het speelt.",
  },
];

/** Zie SkillsPage: 'snelle stijger' en 'Afnemend' meten verschillende dingen. */
const STIJGER_EN_KRIMP =
  "Allebei tegelijk, en dat kan: 'snelle stijger' zegt hoe hard het SIGNAAL " +
  "groeit, 'Afnemend' dat de VRAAG krimpt. Er wordt hier dus druk geschreven " +
  "over het verdwijnen van dit werk.";

// K4 (bouwrun R1): ook "zelfde bron, afgeleid" — één woordenlijst in lib/schermen.
const RUNG_LABEL: Record<string, string> = TREDE_LABEL;

/** De statuswoorden in gewone taal — dezelfde als in de lijst (backlog 30: één
 *  as, één woordenlijst). Het paneel toonde hier de ruwe contract-enum. */
const STATUS_LABEL: Record<string, string> = {
  Emerging: "Opkomend",
  Established: "Gevestigd",
  Declining: "Afnemend",
};

const BADGE_LABEL: Record<string, string> = {
  trending: "snelle stijger",
  quickwin: "quick win",
  urgent: "urgent",
  priority: "topprioriteit",
};

const relRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  textAlign: "left",
  background: color.surfaceQuote,
  border: `1px solid ${color.line}`,
  borderRadius: radius.quote,
  padding: "8px 10px",
  fontSize: size.meta,
  fontFamily: "inherit",
  color: color.body,
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "datum onbekend";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "datum onbekend";
  return d.toLocaleDateString("nl-NL", { month: "short", year: "numeric" });
}

/**
 * De gegenereerde alinea als losse regels, zoals de dummy zijn lijstje toont
 * (V1 r. 1308-1315).
 *
 * Dit knipt alleen op zinsgrenzen — er komt geen woord bij en er gaat er geen
 * af. De dummy had hier échte bullets mét voorbeeld, maar die waren
 * hardgecodeerde voorbeeldtekst; onze tekst is per vaardigheid geschreven en
 * bestaat uit twee tot drie zinnen. Wil je er echte punten mét voorbeeld van
 * maken, dan is dat een generatieronde, geen opmaakkeuze — bewust uitgesteld
 * bij de planreview van 2026-08-24.
 */
function alsRegels(tekst: string): string[] {
  const delen = tekst
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-Þ])/)
    .map((s) => s.trim())
    .filter(Boolean);
  // Eén zin, of juist een hele lap: dan is knippen geen winst.
  return delen.length >= 2 && delen.length <= 5 ? delen : [tekst];
}

/** Eén cijfer uit het drieluik boven het paneel (V1 r. 1319-1338). */
function Kerncijfer({
  label,
  waarde,
  sub,
  uitleg,
}: {
  label: string;
  waarde: string;
  sub: string;
  uitleg: string;
}) {
  return (
    <div style={{ padding: "12px 14px", background: color.surfaceQuote, minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
        <InfoIcon tooltip={uitleg} />
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: weight.bold,
          color: color.ink,
          letterSpacing: "-0.03em",
          lineHeight: 1,
        }}
      >
        {waarde}
      </div>
      <div style={{ fontSize: 11, color: color.faint, marginTop: 4 }}>{sub}</div>
    </div>
  );
}

/** Eén bewijskaart: bron, gezag, datum, citaat — en een klik naar het brondetail. */
function Bewijskaart({
  item,
  onOpenSource,
}: {
  item: Evidence;
  onOpenSource?: (sourceId: string) => void;
}) {
  const [hover, setHover] = useState(false);
  const klikbaar = Boolean(item.sourceId && onOpenSource);
  const zonderTitel = !item.title;
  return (
    <div
      onClick={() => klikbaar && onOpenSource!(item.sourceId!)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover && klikbaar ? color.surfaceAlt : color.surface,
        border: `1px solid ${hover && klikbaar ? color.disabled : color.lineFaint}`,
        borderRadius: radius.quote,
        padding: "10px 12px",
        marginBottom: 6,
        cursor: klikbaar ? "pointer" : "default",
        transition: "all 0.15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginBottom: 4,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: weight.medium, color: color.ink }}>
          {item.publisher}
        </span>
        <span style={{ fontSize: 11, color: color.faint }}>{fmtDate(item.date)}</span>
        {/* Methode v3: dezelfde bronregel als bij een trend — wie · waarop ·
            punten. Zonder de velden staat het gezagscijfer er als voorheen. */}
        {heeftBewijsvelden(item) ? (
          <Bronregel bron={item} />
        ) : (
          <span
            style={{ fontSize: 11, color: color.faint }}
            title="Gezagscijfer van de uitgever, 0-10 (rubriekdimensie source_credibility)."
          >
            gezag {item.authority}
          </span>
        )}
        {!heeftBewijsvelden(item) && <BetaalmuurPil bron={item} />}
        {/* L2 (bouwrun R1): afgeleid zonder opgeslagen redenering. */}
        {item.derivation === "afgeleid" && item.onderbouwingOntbreekt && (
          <ReasonBadge
            text={ONDERBOUWING_ONTBREEKT}
            tone="amber"
            title="Het platform leidde deze vaardigheid af, maar bewaarde geen redenering waarom. Lees het citaat zelf voordat je erop bouwt."
          />
        )}
        {item.derivation === "afgeleid" && (
          <ReasonBadge
            text="afgeleid"
            title={
              "De bron benoemt deze vaardigheid niet met zoveel woorden; de leespas " +
              "heeft hem uit het citaat afgeleid. Bij 'expliciet' staat hij er letterlijk."
            }
          />
        )}
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 11, color: color.accentBlue, marginLeft: "auto" }}
          >
            origineel openen
          </a>
        )}
      </div>
      <p
        style={{
          fontSize: 12,
          color: color.muted,
          lineHeight: 1.55,
          borderLeft: `2px solid ${color.border}`,
          paddingLeft: 9,
        }}
      >
        “{item.quote}”
      </p>
      {item.practiceQuote && (
        <p
          style={{
            fontSize: 12,
            color: color.soft,
            lineHeight: 1.55,
            borderLeft: `2px solid ${color.border}`,
            paddingLeft: 9,
            marginTop: 6,
          }}
        >
          <span style={{ color: color.faint, fontSize: 11 }}>in de praktijk: </span>
          “{item.practiceQuote}”
        </p>
      )}
      <p
        style={{
          fontSize: 11,
          color: zonderTitel ? color.disabled : color.faint,
          fontStyle: zonderTitel ? "italic" : "normal",
          marginTop: 5,
        }}
      >
        {item.title || "deze bron gaf geen titel mee bij het ophalen"}
      </p>
    </div>
  );
}

export function SkillDetail({
  skill,
  trends,
  trendsLoading = false,
  evidence,
  evidenceLoading = false,
  onOpenSource,
  onClose,
}: {
  skill: Skill;
  /** De trends die deze vaardigheid vragen of raken (de brug, backlog 11). */
  trends: SkillTrendRef[];
  /** Nog aan het ophalen: dan is "geen trend gekoppeld" een leugen. */
  trendsLoading?: boolean;
  /** De bronnen mét citaat; komt van het detail-endpoint, niet uit de lijst. */
  evidence?: Evidence[];
  evidenceLoading?: boolean;
  /** Doorklikken naar het brondetail. Zonder dit blijven de kaarten leesbaar
   *  maar niet klikbaar — geen dode klik. */
  onOpenSource?: (sourceId: string) => void;
  onClose: () => void;
}) {
  const [openScore, setOpenScore] = useState<ScoreKey | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [skill.id]);

  const scores = skill.scores;
  const rationale = skill.scoreRationale ?? {};
  const divider = <div style={{ height: 1, background: color.lineFaint, marginBottom: 20 }} />;
  const kop = skill.displayTitle || skill.name;
  const krimp = skill.status === "Declining";
  const bewijs = evidence ?? skill.evidence ?? [];
  const zonderVraag = scores?.priority_basis === "zonder_trendvraag";

  return (
    <div ref={scroller} style={{ height: "100%", overflowY: "auto" }}>
      <PanelHeader onClose={onClose}>
        <ReasonBadge text={skill.type} title="Soort vaardigheid." />
        <ReasonBadge
          text={
            (krimp ? "↓ " : "") + (STATUS_LABEL[skill.status] ?? skill.status)
          }
          tone={krimp ? "amber" : "neutral"}
          title={
            krimp && skill.reason === "trending"
              ? STIJGER_EN_KRIMP
              : krimp
                ? "Afnemend: bronnen beschrijven krimp in de vraag naar deze vaardigheid. " +
                  "Dat is een bevinding, geen voetnoot — dit werk verdwijnt."
                : skill.status === "Emerging"
                  ? "Opkomend: deze vaardigheid is nieuw of groeit snel."
                  : "Gevestigd: al breed gevraagd."
          }
        />
        <ReasonBadge text={skill.sector} />
        {skill.quickWin && (
          <ReasonBadge
            text="quick win"
            tone="amber"
            title="Veel impact voor weinig investering (impact ≥ 8 en effort ≤ 6, config/prioriteit.yaml)."
          />
        )}
        {skill.category && <ReasonBadge text={skill.category} />}
        {skill.categoryProposed && (
          <ReasonBadge
            text={`voorstel: ${skill.categoryProposed}`}
            tone="amber"
            title={
              "Voorgestelde categorie. De pijplijn mag een nieuwe categorie voorstellen; " +
              "hij wordt pas vastgesteld als vijf onafhankelijke uitgevers hem dragen."
            }
          />
        )}
        {skill.reason && (
          <ReasonBadge
            text={BADGE_LABEL[skill.reason] ?? skill.reason}
            tone="blue"
            title="Waarom deze vaardigheid wordt uitgelicht."
          />
        )}
      </PanelHeader>

      <div style={{ padding: 24 }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.3,
            letterSpacing: "-0.02em",
            marginBottom: skill.displayTitle && skill.displayTitle !== skill.name ? 4 : 14,
          }}
        >
          {kop}
        </h2>
        {skill.displayTitle && skill.displayTitle !== skill.name && (
          <p style={{ fontSize: 12, color: color.faint, marginBottom: 14 }}>
            vastgelegde titel: {skill.name}
          </p>
        )}

        <p
          style={{
            fontSize: size.body,
            color: color.muted,
            lineHeight: 1.75,
            marginBottom: 20,
          }}
        >
          {skill.description}
        </p>

        {/* Wat je straks kunt — in de dummy staat dit hier, direct onder de
            omschrijving, en niet verderop onder de cijfers. */}
        <SectionLabel>Wat je straks kunt</SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {skill.education?.future ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {alsRegels(skill.education.future).map((regel, i) => (
                <div
                  key={i}
                  style={{
                    paddingLeft: 14,
                    borderLeft: `2px solid ${color.border}`,
                    fontSize: size.meta,
                    color: color.body,
                    lineHeight: 1.65,
                  }}
                >
                  {regel}
                </div>
              ))}
            </div>
          ) : (
            <EmptyReason reason="nog niet beschreven voor deze vaardigheid" />
          )}
        </div>

        {divider}

        {/* Het drieluik uit de dummy (V1 r. 1319-1338). Vervangt het blok
            "Meting", dat dezelfde getallen toonde maar op een plek waar niemand
            ze zocht. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            border: `1px solid ${color.lineFaint}`,
            borderRadius: radius.block,
            overflow: "hidden",
            gap: 1,
            background: color.lineFaint,
            marginBottom: 20,
          }}
        >
          <Kerncijfer
            label="Prioriteit"
            waarde={
              typeof scores?.priority === "number" ? komma(scores.priority) : "—"
            }
            // Een prioriteit die zonder trendvraag is gerekend betekent iets
            // anders dan een volledige (owner-review 2026-08-25). Het cijfer
            // noemt zijn grond in plaats van laag te lijken zonder reden.
            sub={zonderVraag ? "zonder trendvraag" : "bewijskracht"}
            uitleg={
              zonderVraag
                ? "Geen enkele trend vraagt deze vaardigheid, dus de zwaarste term " +
                  "van de formule (55%: hoe zwaar wegen de trends die erom vragen) " +
                  "was niet te meten. Het cijfer is herschaald over wat wél meetbaar " +
                  "is — breedte van het bewijs en leerbaarheid. Het zegt dus iets " +
                  "over het BEWIJS, niet over het belang: kijk naast dit cijfer naar " +
                  "impact. Dit geldt voor 997 van de 1.762 vaardigheden."
                : "Vraag uit de trends, breedte van het bewijs en de investering " +
                  "samen. Berekend uit config/prioriteit.yaml, niet geschat — en dus " +
                  "na te rekenen."
            }
          />
          <Kerncijfer
            label="Trending"
            waarde={
              typeof scores?.trending === "number" ? komma(scores.trending) : "—"
            }
            sub="van 10"
            uitleg="Hoe hard het signaal onder deze vaardigheid groeit, 0-10."
          />
          <Kerncijfer
            label="Bewijs"
            waarde={String(skill.sourceCount)}
            sub={`${skill.trendCount} ${trendlaagAan() ? (skill.trendCount === 1 ? "bevinding" : "bevindingen") : skill.trendCount === 1 ? "trend" : "trends"}`}
            uitleg={
              "Het aantal onafhankelijke bronnen dat deze vaardigheid draagt. " +
              "Elk daarvan staat hieronder met zijn letterlijke citaat."
            }
          />
        </div>

        <SectionLabel>Scores</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginBottom: 20,
          }}
        >
          {SCORE_CARDS.map((card) => {
            const value = scores?.[card.key];
            const uitleg = rationale[card.key];
            const open = openScore === card.key;
            return (
              <div
                key={card.key}
                onClick={() => uitleg && setOpenScore(open ? null : card.key)}
                title={uitleg ? "Klik voor de motivering" : card.hint}
                style={{
                  background: color.surfaceQuote,
                  border: `1px solid ${open ? color.disabled : "transparent"}`,
                  borderRadius: radius.block,
                  padding: 12,
                  cursor: uitleg ? "pointer" : "default",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 10,
                    fontWeight: weight.medium,
                    color: color.faint,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  {card.label}
                  <InfoIcon tooltip={card.hint} />
                </div>
                {typeof value === "number" ? (
                  <>
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: weight.bold,
                        color: color.ink,
                        lineHeight: 1,
                        marginBottom: 8,
                      }}
                    >
                      {komma(value)}
                    </div>
                    <div style={{ height: 3, background: color.border, borderRadius: 99 }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.max(0, Math.min(10, value)) * 10}%`,
                          background: card.color,
                          borderRadius: 99,
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <EmptyReason reason="niet beoordeeld" />
                )}
                {open && uitleg && (
                  <p
                    style={{
                      fontSize: 12,
                      color: color.soft,
                      lineHeight: 1.6,
                      borderTop: `1px solid ${color.border}`,
                      paddingTop: 6,
                      marginTop: 8,
                    }}
                  >
                    {uitleg}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {divider}

        {/* De kop heette "Waarom deze vaardigheid" en zei daarmee niet wat
            eronder stond: een lijst trends, niet een motivering. */}
        <SectionLabel>
          {trendlaagAan() ? "Bevindingen" : "Voortgekomen uit deze trends"}
          {trendsLoading ? "" : ` (${trends.length})`}
        </SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {trendsLoading ? (
            <span style={{ fontSize: size.meta, color: color.faint }}>{trendlaagAan() ? "bevindingen" : "trends"} ophalen…</span>
          ) : trends.length === 0 ? (
            <EmptyReason
              reason={
                "Geen trend gekoppeld: geen bron legt deze vaardigheid expliciet aan een " +
                "trend vast. Dat is bij 998 van de 1.762 vaardigheden zo — de koppeling " +
                "wordt bewezen, niet aangenomen. De bronnen hieronder staan er wél."
              }
            />
          ) : trendlaagAan() ? (
            <BevindingenPerTrendOud bevindingen={trends.map((t) => ({ id: t.id, titel: t.title }))} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {trends.map((t) => {
                const zwak = t.relation === "raakt";
                return (
                  <div key={t.id} style={relRow}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.title}
                    </span>
                    <span style={{ fontSize: 11, color: color.faint, flexShrink: 0 }}>
                      {/* W8-regel 3: op de zwakkere trede nooit causale taal. */}
                      {zwak ? "raakt aan" : "vraagt om"}
                    </span>
                    {t.evidenceRung === "zelfde_bron_afgeleid" ? (
                      <ReasonBadge
                        text={TREDE_LABEL.zelfde_bron_afgeleid}
                        tone="neutral"
                        title={TREDE_UITLEG.zelfde_bron_afgeleid}
                      />
                    ) : t.evidenceRung && (
                      <ReasonBadge
                        text={zwak ? "zwakkere grond" : "harde grond"}
                        tone={zwak ? "neutral" : "blue"}
                        title={`Bewijstrede: ${RUNG_LABEL[t.evidenceRung] ?? t.evidenceRung}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {divider}

        <SectionLabel>
          Bewijs{evidenceLoading ? "" : ` (${bewijs.length})`}
        </SectionLabel>
        <div style={{ marginBottom: 20 }}>
          {evidenceLoading ? (
            <span style={{ fontSize: size.meta, color: color.faint }}>bronnen ophalen…</span>
          ) : bewijs.length === 0 ? (
            <EmptyReason
              reason={
                "Geen citaat vastgelegd bij deze vaardigheid. Dat komt bij 14 van de " +
                "1.762 voor: de leespas herkende de vaardigheid, maar geen enkele passage " +
                "haalde de citaatpoort. Zonder verifieerbaar citaat tonen we er liever geen."
              }
            />
          ) : (
            <>
              <p style={{ fontSize: 11, color: color.faint, marginBottom: 8, lineHeight: 1.5 }}>
                Elk citaat staat letterlijk in de bron; klik een kaart om de bron zelf te
                openen.
              </p>
              {bewijs.map((item) => (
                <Bewijskaart key={item.id} item={item} onOpenSource={onOpenSource} />
              ))}
            </>
          )}
        </div>

        {divider}

        <SectionLabel>Opleiding</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {skill.taxonomy?.creboCodes?.length ? (
            <div style={{ fontSize: size.meta, color: color.body }}>
              Crebo: {skill.taxonomy.creboCodes.join(", ")}
            </div>
          ) : (
            <ComingSoon
              title="Beschikbare scholing"
              reason={
                "Er is geen contract, geen tabel en geen bron voor opleidingsaanbod. De " +
                "koppeling loopt via de crebo-codes van CompetentNL; die matching is " +
                "bewust uitgesteld tot er voorbeelden liggen om op te oordelen."
              }
            />
          )}
          <SkillRegelingen sector={skill.sector} />
        </div>
      </div>
    </div>
  );
}
